import { Link } from "react-router-dom";
import { ArrowRight, Globe2 } from "lucide-react";
import { motion } from "framer-motion";
import PriceTicker from "../components/PriceTicker";
import Icon from "../components/Icon";
import { useSiteContent, resolveImageUrl } from "../context/ContentContext";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  const { content } = useSiteContent();
  const { hero, intro, trust_strip, services, products, stats, images, company, home_copy } = content;
  const homeCopy = home_copy || {};

  return (
    <>
      {/* HERO */}
      <section className="relative hero-radial overflow-hidden" data-testid="hero-section">
        <div className="world-map-bg" />
        <div className="container-x grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-0 lg:min-h-[80vh] pt-4 sm:pt-8 pb-14 sm:pb-20 relative">
          <div className="relative z-10">
            <motion.div {...fadeUp} className="eyebrow mb-6">{hero.eyebrow}</motion.div>
            <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }}
              className="font-display font-bold leading-[1.05] theme-text-primary text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight">
              {hero.headline_part1}<br />
              <span style={{ color: "var(--gold)" }}>{hero.headline_gold1}</span> {hero.headline_join}{" "}
              <span style={{ color: "var(--gold)" }}>{hero.headline_gold2}</span>
            </motion.h1>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="mt-5 sm:mt-7 theme-text-secondary text-base sm:text-lg max-w-xl leading-relaxed">
              {hero.subheadline || company.short}
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.25 }}
              className="mt-8 sm:mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <Link to="/contact" className="btn-primary w-full sm:w-auto justify-center" data-testid="hero-cta-quote">
                {hero.cta_primary} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products" className="btn-secondary w-full sm:w-auto justify-center" data-testid="hero-cta-products">
                {hero.cta_secondary} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="relative">
            <div className="relative aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl gold-ring">
              <img src={resolveImageUrl(images.heroGold)} alt="Investment-grade gold bars" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-transparent" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-end gap-3">
                <div className="theme-badge backdrop-blur-md rounded-2xl px-4 sm:px-5 py-3 shadow-lg">
                  <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold">{hero.badge_gold_label}</div>
                  <div className="font-display text-lg sm:text-xl font-bold theme-text-primary">{hero.badge_gold_value}</div>
                </div>
                <div className="theme-badge backdrop-blur-md rounded-2xl px-4 sm:px-5 py-3 shadow-lg">
                  <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold">{hero.badge_silver_label}</div>
                  <div className="font-display text-lg sm:text-xl font-bold theme-text-primary">{hero.badge_silver_value}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <PriceTicker />

      {/* TRUST STRIP */}
      <section className="theme-bg-primary border-b theme-border py-8" data-testid="trust-strip">
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(240px,1fr))] gap-6 sm:gap-8">
          {trust_strip.map((t, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full theme-icon-chip flex items-center justify-center shrink-0">
                <Icon name={t.icon} className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold theme-text-primary text-sm">{t.title}</div>
                <div className="text-xs theme-text-muted mt-1 leading-relaxed">{t.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO + ISTANBUL CARD */}
      <section className="section-pad" data-testid="intro-section">
        <div className="container-x grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <div className="eyebrow mb-5">{intro.eyebrow}</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold theme-text-primary leading-tight tracking-tight whitespace-pre-line">
              {intro.heading}
            </h2>
            <div className="divider-gold mt-6 mb-8" />
            <p className="theme-text-secondary text-base sm:text-lg leading-relaxed">{intro.paragraph1}</p>
            <p className="theme-text-secondary text-base sm:text-lg leading-relaxed mt-4">{intro.paragraph2}</p>
            <Link to="/about" className="btn-secondary mt-8 w-full sm:w-auto justify-center" data-testid="intro-about-link">
              {intro.cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="relative">
            <div className="relative aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl">
              <img src={resolveImageUrl(images.istanbul)} alt="Istanbul skyline" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-auto theme-surface rounded-2xl sm:rounded-full px-4 sm:px-5 py-3 shadow-lg flex items-center gap-3 max-w-[calc(100%-2rem)]">
                <div className="w-9 h-9 rounded-full theme-icon-chip flex items-center justify-center">
                  <Globe2 className="w-4 h-4" />
                </div>
                <div className="leading-tight">
                  <div className="text-[10px] tracking-[0.22em] theme-text-accent font-semibold">{intro.badge_label}</div>
                  <div className="text-sm font-bold theme-text-primary">{intro.badge_text}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="theme-bg-secondary section-pad" data-testid="services-section">
        <div className="container-x">
          <div className="flex flex-wrap justify-between items-end gap-4 sm:gap-6 mb-10 sm:mb-12">
            <div>
              <div className="eyebrow mb-4">{homeCopy.services_eyebrow}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold theme-text-primary tracking-tight">
                {homeCopy.services_title_part1}{" "}
                <span className="theme-text-accent">{homeCopy.services_title_highlight}</span>{" "}
                {homeCopy.services_title_part2}
              </h2>
            </div>
            <Link to="/services" className="btn-ghost" data-testid="view-all-services">
              {homeCopy.services_cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(260px,1fr))] gap-5 sm:gap-6">
            {services.slice(0, 3).map((s, i) => (
              <motion.div key={s.key || i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 * i }}
                className="card-soft p-6 sm:p-8" data-testid={`service-card-${s.key}`}>
                <div className="w-14 h-14 rounded-2xl theme-icon-chip flex items-center justify-center mb-6">
                  <Icon name={s.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display text-2xl font-bold theme-text-primary mb-3">{s.title}</h3>
                <p className="theme-text-secondary text-sm leading-relaxed">{s.short}</p>
                <Link to="/services" className="btn-ghost mt-6">LEARN MORE <ArrowRight className="w-4 h-4" /></Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section-pad" data-testid="products-preview">
        <div className="container-x">
          <div className="flex flex-wrap justify-between items-end gap-4 sm:gap-6 mb-10 sm:mb-12">
            <div>
              <div className="eyebrow mb-4">{homeCopy.products_eyebrow}</div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold theme-text-primary tracking-tight">
                {homeCopy.products_title_part1}{" "}
                <span className="theme-text-accent">{homeCopy.products_title_highlight}</span>
                {homeCopy.products_title_part2 ? ` ${homeCopy.products_title_part2}` : ""}
              </h2>
            </div>
            <Link to="/products" className="btn-ghost" data-testid="view-all-products">
              {homeCopy.products_cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(240px,1fr))] gap-5">
            {products.map((p, i) => {
              const imgSrc = images[p.image] || p.image;
              return (
                <motion.div key={p.title || i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 * i }}
                  className="card-soft overflow-hidden" data-testid={`product-card-${(p.title || "").toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="aspect-[4/3] theme-bg-secondary overflow-hidden">
                    <img src={resolveImageUrl(imgSrc)} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                  </div>
                  <div className="p-5 sm:p-6">
                    <h3 className="font-display text-xl font-bold theme-text-primary">{p.title}</h3>
                    <p className="theme-text-muted text-sm mt-2 leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <section className="theme-bg-dark py-20 text-[var(--interactive-text)]" data-testid="stats-band">
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 md:[grid-template-columns:repeat(auto-fit,minmax(180px,1fr))] gap-8 sm:gap-10">
          {stats.map((s, i) => (
            <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 * i }}
              className="text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-4xl sm:text-5xl md:text-6xl font-bold theme-text-accent mb-3 tracking-tight">{s.value}</div>
              <div className="text-xs theme-panel-dark-subtle uppercase tracking-[0.18em]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad" data-testid="bottom-cta">
        <div className="container-x">
          <div className="rounded-3xl theme-gradient-dark p-7 sm:p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: "var(--border-gold)" }} />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="eyebrow mb-4" style={{ color: "var(--gold-hover)" }}>{homeCopy.bottom_cta_eyebrow}</div>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight tracking-tight">
                  {homeCopy.bottom_cta_title}
                </h2>
              </div>
              <div className="md:text-right">
                <p className="theme-panel-dark-muted mb-6 md:max-w-md md:ml-auto">{homeCopy.bottom_cta_body}</p>
                <Link to="/contact" className="btn-primary w-full sm:w-auto justify-center" data-testid="bottom-cta-button">
                  {homeCopy.bottom_cta_button} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
